import { useAuth, useUser } from '@clerk/react';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError, apiFetch } from '@/api/client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { EXPORTS_LOCKED_TOOLTIP, billingUi, type BillingFields } from '@/lib/portalBilling';
import { portalPath } from '@/lib/portalPath';
import { approvalSubtypeBoxes, type ApprovalReasonRecovery } from '@/lib/portalRecovery';

export type PublishedEngagement = {
  name: string;
  period_start: string;
  period_end: string;
  source_type: string | null;
  published_at: string;
  default_province: string | null;
  transaction_count: number;
  rule_set_id: string | null;
  rule_set_version: number | null;
};

export type PortalSummary = {
  customer_slug: string;
  customer_name: string;
  /** Refundable dollars — sum of approved findings scoped to
   *  recovery-outcome categories only. The headline "money back"
   *  number on the report card. */
  total_approved_recovery_cad: string;
  /** Exposure dollars — sum of approved findings scoped to
   *  compliance-risk-outcome categories (rate gaps, doc-gap-
   *  unrecoverable, etc.). Not refundable; surfaced alongside
   *  recovery so the report tells the full story. */
  total_approved_compliance_risk_cad: string;
  /** Counts of approved findings in each outcome bucket. */
  approved_recovery_finding_count: number;
  approved_compliance_risk_finding_count: number;
  /** The invoice-documented subset of the approved recovery figures —
   *  approved recovery $ (and count) whose transactions carry an
   *  attached invoice document. Always <= the approved figures. */
  invoice_documented_recovery_cad: string;
  invoice_documented_recovery_finding_count: number;
  /** Reason-based confirmed/estimated split of approved recovery — the
   *  practitioner-stated basis. `confirmed` = approvals marked
   *  `invoice_confirmed`; `estimated` = the remainder. The two sum to the
   *  approved total. This is the authoritative "backed by invoice" figure. */
  confirmed_recovery_cad: string;
  confirmed_recovery_finding_count: number;
  estimated_recovery_cad: string;
  estimated_recovery_finding_count: number;
  /** Per-approval-reason breakdown of the approved recovery total — one entry
   *  per basis of confidence, strongest first. Rendered as the subtype boxes
   *  that replace the confirmed/estimated prose. */
  approved_recovery_by_reason: ApprovalReasonRecovery[];
  /** Total findings the engine emitted (any status, any outcome). */
  gross_finding_count: number;
  /** Findings the practitioner triaged (approved + rejected, both outcomes). */
  reviewed_finding_count: number;
  /** Subset of `reviewed_finding_count` that were explicitly rejected. */
  rejected_finding_count: number;
  /** Per-outcome denominators for the two approval-rate stats. Scoped
   *  by category so each rate is honest — `approved $ / gross $`
   *  within its own bucket, not inflated by the other one. */
  gross_recovery_cad: string;
  gross_compliance_risk_cad: string;
  has_published_engagement: boolean;
  latest_engagement: PublishedEngagement | null;
  is_preview: boolean;
} & BillingFields;

/** Which version of the card to render.
 *
 * - `signed`: pull the real numbers via `/api/portal/summary` and render
 *   the full report card. The default for the customer's `/portal` route
 *   when their engagement is `published`.
 * - `prospect`: do NOT call the API. Render a layout-only "tease" with
 *   blurred placeholders, the trust-signal tiles, and a CTA to sign the
 *   engagement. The default for the customer's `/portal` route when no
 *   engagement has been published; also reachable from the practitioner
 *   preview page via the `?view=prospect` toggle.
 *
 * Prospect mode intentionally skips the network call so a curious
 * customer can't inspect their would-be numbers via DevTools before the
 * engagement is signed.
 */
export type PortalCardView = 'prospect' | 'signed';

interface Props {
  /** When set, fetches the portal summary as a practitioner preview of
   *  this account slug. Caller must already be a practitioner — backend
   *  rejects the param for customers with 403. Ignored when
   *  `view === 'prospect'`. */
  previewSlug?: string;
  /** Defaults to `signed` for backwards compatibility with the existing
   *  customer route. */
  view?: PortalCardView;
}

export function PortalSummaryCard({ previewSlug, view = 'signed' }: Props = {}) {
  if (view === 'prospect') {
    return <ProspectCard />;
  }
  return <SignedCard previewSlug={previewSlug} />;
}

// ─── Signed (real data) ───────────────────────────────────────────────

function SignedCard({ previewSlug }: { previewSlug?: string }) {
  const { getToken, userId } = useAuth();
  const { user: clerkUser } = useUser();
  const summary = useQuery({
    queryKey: ['portal', 'summary', userId, previewSlug ?? null],
    enabled: !!userId,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        return false;
      }
      return failureCount < 1;
    },
    queryFn: async () => {
      const token = await getToken();
      if (!token) throw new Error('Not signed in');
      return apiFetch<PortalSummary>(portalPath('/api/portal/summary', previewSlug), {
        idToken: token,
      });
    },
  });

  if (summary.isLoading) {
    return (
      <Card className="max-w-5xl">
        <CardHeader>
          <CardTitle>Loading…</CardTitle>
        </CardHeader>
      </Card>
    );
  }

  if (summary.isError || !summary.data) {
    const is403 = summary.error instanceof ApiError && summary.error.status === 403;
    const is404 = summary.error instanceof ApiError && summary.error.status === 404;
    const email = clerkUser?.primaryEmailAddress?.emailAddress;
    return (
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>
            {previewSlug && is404
              ? 'Customer not found'
              : is403
                ? previewSlug
                  ? 'Preview unavailable'
                  : 'Your account is being set up'
                : 'Unable to load summary'}
          </CardTitle>
          <CardDescription>
            {previewSlug && is404 ? (
              <>
                No account with slug <strong>{previewSlug}</strong>.
              </>
            ) : is403 && previewSlug ? (
              "You don't have access to preview this account."
            ) : is403 ? (
              <>
                Sign-in succeeded, but your account hasn&apos;t finished provisioning yet. This
                usually takes a few seconds after accepting your invitation. If the page still looks
                like this after a refresh, ask your InputRecovery practitioner to verify the invite
                for {email ? <strong>{email}</strong> : 'your email'}.
              </>
            ) : summary.error instanceof Error ? (
              summary.error.message
            ) : (
              'Unknown error.'
            )}
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const data = summary.data;
  return <ReportCard data={data} previewSlug={previewSlug} />;
}

// ─── Report card — shared shape, rendered with real or blurred data ──

function ReportCard({ data, previewSlug }: { data: PortalSummary; previewSlug?: string }) {
  // Account has no published engagement yet — fall back to the prospect
  // tease rather than rendering an empty/blank report.
  if (!data.has_published_engagement || data.latest_engagement === null) {
    return <ProspectCard customerName={data.customer_name} />;
  }

  const eng = data.latest_engagement;
  const reviewed = data.reviewed_finding_count;
  const gross = data.gross_finding_count;
  const rejected = data.rejected_finding_count;
  const approvedRecoveryCount = data.approved_recovery_finding_count;
  const approvedComplianceCount = data.approved_compliance_risk_finding_count;
  const totalApprovedCount = approvedRecoveryCount + approvedComplianceCount;
  const recoveryApprovedCad = Number(data.total_approved_recovery_cad);
  const recoveryGrossCad = Number(data.gross_recovery_cad);
  const complianceApprovedCad = Number(data.total_approved_compliance_risk_cad);
  const complianceGrossCad = Number(data.gross_compliance_risk_cad);
  const reviewedPct = gross > 0 ? Math.round((reviewed / gross) * 100) : 0;
  const recoveryApprovalRatePct =
    recoveryGrossCad > 0 ? Math.round((recoveryApprovedCad / recoveryGrossCad) * 100) : 0;
  const complianceApprovalRatePct =
    complianceGrossCad > 0
      ? Math.round((complianceApprovedCad / complianceGrossCad) * 100)
      : 0;

  return (
    <Card className="max-w-5xl overflow-hidden">
      <TopMetadataBar
        engagementName={eng.name}
        periodLabel={`${formatMonthYear(eng.period_start)} to ${formatMonthYear(eng.period_end)}`}
        generatedLabel={formatLongDate(eng.published_at)}
      />
      <CardHeader className="gap-2 pb-4">
        <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
          <div>
            <CardTitle className="text-3xl font-semibold tracking-tight text-brand-navy">
              {data.customer_name}
            </CardTitle>
            <CardDescription className="mt-1 text-sm text-brand-slate">
              GST/HST Recovery Report · {fmtCount(eng.transaction_count)} transactions analyzed ·{' '}
              {formatProvinceLine(eng.default_province)}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <ReportDownloadButton
              label="Export PDF"
              path="/api/portal/report.pdf"
              filename={`recovery-report-${data.customer_slug}.pdf`}
              previewSlug={previewSlug}
              locked={billingUi(data).exportsLocked}
            />
            <ReportDownloadButton
              label="Export CSV"
              path="/api/portal/findings.csv"
              filename={`recoveries-${data.customer_slug}.csv`}
              previewSlug={previewSlug}
              locked={billingUi(data).exportsLocked}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="grid gap-6">
        <div className="grid gap-3">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-800">
            Refundable recovery
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <BigStat
              label="Net Approved Recovery"
              value={fmtCad(recoveryApprovedCad)}
              subtext={`${fmtCount(approvedRecoveryCount)} findings approved`}
              tone="emerald"
              badge="Defensible"
            />
            <BigStat
              label="Gross Recovery Flagged"
              value={fmtCad(recoveryGrossCad)}
              subtext="across published runs"
            />
            <BigStat
              label="Recovery Approval Rate"
              value={`${recoveryApprovalRatePct}%`}
              subtext={`${fmtCadShort(recoveryApprovedCad)} of ${fmtCadShort(recoveryGrossCad)}`}
            />
          </div>
          <div className="grid gap-2">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-brand-slate">
              By basis of confidence
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {approvalSubtypeBoxes(data.approved_recovery_by_reason).map((box) => (
                <BigStat
                  key={box.reason}
                  label={box.label}
                  value={fmtCad(box.amountCad)}
                  subtext={`${fmtCount(box.findingCount)} findings approved`}
                  tone={box.isStrongest ? 'emerald' : undefined}
                  badge={box.isStrongest ? 'Strongest evidence' : undefined}
                />
              ))}
            </div>
            <p className="text-xs text-brand-slate">
              Invoice-confirmed recovery is backed by a reviewed invoice, the strongest evidence
              tier when filing with the CRA. Supplier-sampled and practitioner-judgment recovery is
              supported by representative sampling or expert ledger review.
            </p>
          </div>
        </div>
        <div className="grid gap-3">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-800">
            Compliance exposure (not refundable)
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <BigStat
              label="Net Approved Compliance"
              value={fmtCad(complianceApprovedCad)}
              subtext={`${fmtCount(approvedComplianceCount)} findings approved`}
              tone="amber"
              badge="Informational"
            />
            <BigStat
              label="Gross Compliance Flagged"
              value={fmtCad(complianceGrossCad)}
              subtext="across published runs"
            />
            <BigStat
              label="Compliance Approval Rate"
              value={`${complianceApprovalRatePct}%`}
              subtext={`${fmtCadShort(complianceApprovedCad)} of ${fmtCadShort(complianceGrossCad)}`}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-1">
          <BigStat
            label="Reviewed (all findings)"
            value={`${fmtCount(reviewed)} / ${fmtCount(gross)}`}
            subtext={`${reviewedPct}% · ${fmtCount(totalApprovedCount)} approved · ${fmtCount(rejected)} rejected`}
          />
        </div>
        <TrustSignalsRow approvedCount={totalApprovedCount} grossCount={gross} />
      </CardContent>
    </Card>
  );
}

// ─── Prospect (locked, no API call) ──────────────────────────────────

/** Layout-only tease. NEVER fetches anything — a curious customer can't
 *  inspect their would-be numbers via DevTools. Numbers are visually
 *  blurred placeholders; the trust signals + CTA still render. */
function ProspectCard({ customerName }: { customerName?: string } = {}) {
  const { user: clerkUser } = useUser();
  const displayAccountName =
    customerName ??
    clerkUser?.primaryEmailAddress?.emailAddress?.split('@')[1] ??
    'Your engagement';
  return (
    <Card className="max-w-5xl overflow-hidden">
      <TopMetadataBar
        engagementName={<LockedText>Engagement</LockedText>}
        periodLabel={<LockedText>Period</LockedText>}
        generatedLabel={<LockedText>Pending</LockedText>}
      />
      <CardHeader className="gap-2 pb-4">
        <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
          <div>
            <Badge className="border-transparent bg-amber-100 text-[10px] font-semibold uppercase tracking-wider text-amber-900 hover:bg-amber-100">
              Preliminary findings · pre-engagement
            </Badge>
            <CardTitle className="mt-2 text-3xl font-semibold tracking-tight text-brand-navy">
              {displayAccountName}
            </CardTitle>
            <CardDescription className="mt-1 text-sm text-brand-slate">
              GST/HST Recovery Report · <LockedText>X,XXX</LockedText> transactions analyzed ·{' '}
              <LockedText>Province</LockedText>
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" size="sm" variant="outline" disabled title="Available after engagement signed">
              Export PDF
            </Button>
            <Button type="button" size="sm" variant="outline" disabled title="Available after engagement signed">
              Export CSV
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="grid gap-6">
        <div className="grid gap-3">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-800">
            Refundable recovery
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <BigStat
              label="Net Approved Recovery"
              value={<LockedText>C$XXX,XXX</LockedText>}
              subtext={<LockedText>XXX findings approved</LockedText>}
              tone="emerald"
              badge="Sign to unlock"
            />
            <BigStat
              label="Gross Recovery Flagged"
              value={<LockedText>C$XXX,XXX</LockedText>}
              subtext={<LockedText>across runs</LockedText>}
            />
            <BigStat
              label="Recovery Approval Rate"
              value={<LockedText>XX%</LockedText>}
              subtext={<LockedText>C$XXX of C$X.XM</LockedText>}
            />
          </div>
        </div>
        <div className="grid gap-3">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-800">
            Compliance exposure (not refundable)
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <BigStat
              label="Net Approved Compliance"
              value={<LockedText>C$XX,XXX</LockedText>}
              subtext={<LockedText>XX findings approved</LockedText>}
              tone="amber"
              badge="Informational"
            />
            <BigStat
              label="Gross Compliance Flagged"
              value={<LockedText>C$XX,XXX</LockedText>}
              subtext={<LockedText>across runs</LockedText>}
            />
            <BigStat
              label="Compliance Approval Rate"
              value={<LockedText>XX%</LockedText>}
              subtext={<LockedText>C$XX of C$XX,XXX</LockedText>}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-1">
          <BigStat
            label="Reviewed (all findings)"
            value={<LockedText>XXX / XXX</LockedText>}
            subtext={<LockedText>XX% reviewed</LockedText>}
          />
        </div>
        <ProspectCallToAction />
        <TrustSignalsRow approvedCount={null} grossCount={null} />
      </CardContent>
    </Card>
  );
}

function ProspectCallToAction() {
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/60 px-5 py-4">
      <p className="text-sm font-semibold text-amber-900">
        Sign the engagement to unlock your full report.
      </p>
      <p className="mt-1 text-sm text-amber-900/80">
        No upfront cost. Our team finishes the review, then you see every approved
        finding, the supporting evidence, and an exportable filing package. Reach
        out to your InputRecovery contact when you&apos;re ready.
      </p>
    </div>
  );
}

/** Blurred placeholder span — `select-none` keeps text-copy paths from
 *  leaking the placeholder string, `aria-hidden` keeps it out of the
 *  accessibility tree. */
function LockedText({ children }: { children: React.ReactNode }) {
  return (
    <span aria-hidden className="inline-block select-none blur-[5px]">
      {children}
    </span>
  );
}

// ─── Shared building blocks ──────────────────────────────────────────

function TopMetadataBar({
  engagementName,
  periodLabel,
  generatedLabel,
}: {
  engagementName: React.ReactNode;
  periodLabel: React.ReactNode;
  generatedLabel: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-border bg-muted/30 px-6 py-3 text-[11px] uppercase tracking-wider text-brand-slate">
      <MetadataItem label="Engagement" value={engagementName} />
      <MetadataItem label="Period" value={periodLabel} />
      <MetadataItem label="Generated" value={generatedLabel} />
    </div>
  );
}

function MetadataItem({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <span className="flex items-baseline gap-1.5">
      <span className="text-brand-slate/60">{label}</span>
      <span className="font-medium normal-case tracking-normal text-brand-navy">{value}</span>
    </span>
  );
}

function BigStat({
  label,
  value,
  subtext,
  tone,
  badge,
}: {
  label: string;
  value: React.ReactNode;
  subtext: React.ReactNode;
  tone?: 'emerald' | 'amber';
  badge?: string;
}) {
  // Per-tone palette. Default (untoned) tiles use neutral border/text
  // so they recede; toned tiles get a tinted background + matching
  // label/value/subtext color so recovery vs compliance vs neutral
  // read at a glance.
  const palette =
    tone === 'emerald'
      ? {
          card: 'rounded-lg border border-emerald-200 bg-emerald-50/40 px-4 py-3',
          label: 'text-[10px] font-semibold uppercase tracking-wider text-emerald-800',
          value: 'mt-1 text-2xl font-semibold tabular-nums text-emerald-900',
          subtext: 'mt-0.5 text-xs text-emerald-800/80',
          badgeText: 'inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700',
          badgeDot: 'inline-block h-1.5 w-1.5 rounded-full bg-emerald-500',
        }
      : tone === 'amber'
        ? {
            card: 'rounded-lg border border-amber-200 bg-amber-50/40 px-4 py-3',
            label: 'text-[10px] font-semibold uppercase tracking-wider text-amber-800',
            value: 'mt-1 text-2xl font-semibold tabular-nums text-amber-900',
            subtext: 'mt-0.5 text-xs text-amber-800/80',
            badgeText: 'inline-flex items-center gap-1 text-[10px] font-medium text-amber-700',
            badgeDot: 'inline-block h-1.5 w-1.5 rounded-full bg-amber-500',
          }
        : {
            card: 'rounded-lg border border-border bg-background px-4 py-3',
            label: 'text-[10px] font-semibold uppercase tracking-wider text-brand-slate',
            value: 'mt-1 text-2xl font-semibold tabular-nums text-brand-navy',
            subtext: 'mt-0.5 text-xs text-brand-slate',
            badgeText: 'inline-flex items-center gap-1 text-[10px] font-medium text-brand-slate',
            badgeDot: 'inline-block h-1.5 w-1.5 rounded-full bg-brand-slate',
          };

  return (
    <div className={palette.card}>
      <div className="flex items-center justify-between gap-2">
        <p className={palette.label}>{label}</p>
        {badge && (
          <span className={palette.badgeText}>
            <span className={palette.badgeDot} />
            {badge}
          </span>
        )}
      </div>
      <p className={palette.value}>{value}</p>
      <p className={palette.subtext}>{subtext}</p>
    </div>
  );
}

function TrustSignalsRow({
  approvedCount,
  grossCount,
}: {
  approvedCount: number | null;
  grossCount: number | null;
}) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <TrustTile
        title="Audit-defended"
        subtitle="Each finding cites the ETA section that authorizes the ITC."
      />
      <TrustTile
        title="100% reviewed"
        subtitle={
          approvedCount !== null && grossCount !== null ? (
            `${fmtCount(approvedCount)} of ${fmtCount(grossCount)} findings approved by a tax professional.`
          ) : (
            <>
              Every flagged finding reviewed by a tax professional before publish.
            </>
          )
        }
      />
      <TrustTile
        title="Verified before filing"
        subtitle="Sampling on high-value items: not every invoice pulled, but the dollars are defensible."
      />
      <TrustTile
        title="Audit-backed"
        subtitle="If the CRA questions a claim, we stand behind the evidence with you."
      />
    </div>
  );
}

function TrustTile({
  title,
  subtitle,
}: {
  title: string;
  subtitle: React.ReactNode;
}) {
  return (
    <div className="rounded-md border border-border bg-background px-3 py-3">
      <p className="flex items-center gap-1.5 text-sm font-semibold text-brand-navy">
        <span aria-hidden className="inline-block h-3.5 w-3.5 rounded-full bg-sky-200" />
        {title}
      </p>
      <p className="mt-1 text-xs leading-snug text-brand-slate">{subtitle}</p>
    </div>
  );
}

function ReportDownloadButton({
  label,
  path,
  filename,
  previewSlug,
  locked = false,
}: {
  label: string;
  path: string;
  filename: string;
  previewSlug?: string;
  /** Payment gate: disabled + tooltip while the invoice is unpaid.
   *  Belt-and-suspenders with the server-side 403. */
  locked?: boolean;
}) {
  const { getToken } = useAuth();
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClick = async () => {
    setDownloading(true);
    setError(null);
    try {
      const token = await getToken();
      if (!token) throw new Error('Not signed in');
      const res = await fetch(portalPath(path, previewSlug), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        throw new ApiError(res.status, await res.text());
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof ApiError ? `HTTP ${err.status}` : 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={handleClick}
        disabled={downloading || locked}
        title={locked ? EXPORTS_LOCKED_TOOLTIP : undefined}
      >
        {downloading ? 'Preparing…' : label}
      </Button>
      {error && <span className="text-xs text-destructive">{error}</span>}
    </div>
  );
}

// ─── Formatting helpers ──────────────────────────────────────────────

const _cadFmt0 = new Intl.NumberFormat('en-CA', {
  style: 'currency',
  currency: 'CAD',
  maximumFractionDigits: 0,
});

const _intFmt = new Intl.NumberFormat('en-CA');

function fmtCad(n: number): string {
  return _cadFmt0.format(n);
}

/** Compact-ish CAD: `C$167K` / `C$1.38M` for the subtext slots where
 *  the full $ + cents would overflow. */
function fmtCadShort(n: number): string {
  if (n >= 1_000_000) return `C$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `C$${Math.round(n / 1_000)}K`;
  return _cadFmt0.format(n);
}

function fmtCount(n: number): string {
  return _intFmt.format(n);
}

function formatMonthYear(iso: string): string {
  const [y, m] = iso.split('-');
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${months[Number(m) - 1] ?? '?'} ${y}`;
}

function formatLongDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
}

/** "Ontario (HST 13%)" / "British Columbia (GST 5% + PST)" etc.
 *  Conservative: only label the federal HST/GST rate; PST/QST callouts
 *  stay generic until we have province-specific copy. */
function formatProvinceLine(province: string | null): string {
  if (!province) return 'Canada';
  const name = PROVINCE_NAMES[province] ?? province;
  const fedRate = HST_PROVINCES[province] ?? 5;
  const taxName = HST_PROVINCES[province] ? 'HST' : 'GST';
  return `${name} (${taxName} ${fedRate}%)`;
}

const PROVINCE_NAMES: Record<string, string> = {
  AB: 'Alberta',
  BC: 'British Columbia',
  MB: 'Manitoba',
  NB: 'New Brunswick',
  NL: 'Newfoundland and Labrador',
  NS: 'Nova Scotia',
  NT: 'Northwest Territories',
  NU: 'Nunavut',
  ON: 'Ontario',
  PE: 'Prince Edward Island',
  QC: 'Quebec',
  SK: 'Saskatchewan',
  YT: 'Yukon',
};

const HST_PROVINCES: Record<string, number> = {
  ON: 13,
  NB: 15,
  NL: 15,
  NS: 15,
  PE: 15,
};
